import { useEffect, useRef, useState, useCallback } from "react";
import api from "../api";
import { proctorMicBus, isMicInUse } from "../lib/proctorMicBus";

/**
 * useProctor — wires up all browser-side proctoring signals for an assignment.
 *
 * Returns:
 *  - warnings (count of cheating signals so far)
 *  - lastFlash (last UI warning message)
 *  - videoRef (attach to <video> for live preview)
 *  - autoSubmit (true once threshold breached — caller should submit immediately)
 *
 * Caller MUST already have requested camera+mic permission and entered fullscreen.
 */
export function useProctor({ assignmentId, enabled, frameIntervalSec = 15 }) {
  const [warnings, setWarnings] = useState(0);
  const [lastFlash, setLastFlash] = useState("");
  const [autoSubmit, setAutoSubmit] = useState(false);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const noiseCtxRef = useRef(null);
  const noiseTimerRef = useRef(null);
  const frameTimerRef = useRef(null);
  const audioSliceTimerRef = useRef(null);
  const firstSliceTimerRef = useRef(null);
  const activeRecorderRef = useRef(null);

  const flash = useCallback((msg) => {
    setLastFlash(msg);
    // Keep it visible for 10s so the candidate sees it when they come back to
    // the tab (a shorter timeout would auto-dismiss before they return).
    setTimeout(() => setLastFlash(""), 10000);
  }, []);

  const logEvent = useCallback(async (event_type, detail = {}) => {
    if (!enabled || !assignmentId) return;
    try {
      const r = await api.post("/proctor/event", { assignment_id: assignmentId, event_type, detail });
      setWarnings(r.data.warnings ?? 0);
      // Backend renamed `auto_submit` → `warning_threshold_reached` (flag-only policy). Support both for one release.
      if (r.data.warning_threshold_reached ?? r.data.auto_submit) setAutoSubmit(true);
    } catch (e) { /* swallow — proctor must not break the test */ }
  }, [assignmentId, enabled]);

  const frameCountRef = useRef(0);

  // Capture & analyse one frame
  const captureFrame = useCallback(async () => {
    if (!enabled || !videoRef.current || !canvasRef.current) return;
    const v = videoRef.current;
    const c = canvasRef.current;
    if (v.videoWidth === 0) return;
    const w = 480, h = Math.round((v.videoHeight / v.videoWidth) * 480);
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    const dataUrl = c.toDataURL("image/jpeg", 0.6);
    const b64 = dataUrl.split(",")[1];
    // Every 4th frame (≈ every 60s given the 15s cadence) we ALSO ask the
    // backend to compare this frame to the enrolled reference selfie. This
    // catches mid-section candidate handoffs (A does the ID gate, B swaps in
    // during MCQ). Failing frames get logged as `face_identity_mismatch`
    // events in the Integrity Center — no hard-block.
    frameCountRef.current += 1;
    const checkIdentity = frameCountRef.current % 4 === 1;
    try {
      const r = await api.post("/proctor/frame", {
        assignment_id: assignmentId,
        image_base64: b64,
        check_identity: checkIdentity,
      });
      if (r.data.identity && r.data.identity.same_person === false) {
        flash("Face identity check flagged. Please stay in front of the camera as the enrolled candidate.");
      } else if (r.data.suspicious) {
        const msg = r.data.face_count === 0
          ? "No face detected. Please stay in front of the camera."
          : r.data.face_count > 1
            ? "Multiple people in frame. Only the participant should be visible."
            : "Please face the screen directly — your head is turned away.";
        flash(msg);
      }
    } catch (e) { /* ignore */ }
  }, [assignmentId, enabled, flash]);

  // Start camera + mic + listeners
  useEffect(() => {
    if (!enabled) return undefined;
    let mounted = true;

    // iter45 — subscribe to the mic-check bus. When MicCheckGate acquires
    // the mic, we immediately stop any in-flight slice recorder so the two
    // MediaRecorders can't contend for the mic hardware. The noise analyser
    // keeps running (it's passive and causes no hardware contention), so
    // mic-check RMS activity still appears on our proctoring HUD if needed.
    const unsubBus = proctorMicBus.subscribe((ev) => {
      if (ev === "acquire") {
        if (activeRecorderRef.current) {
          try {
            if (activeRecorderRef.current.state && activeRecorderRef.current.state !== "inactive") {
              activeRecorderRef.current.stop();
            }
          } catch (_) { /* noop */ }
          activeRecorderRef.current = null;
        }
      }
    });

    const start = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480 },
          audio: true,
        });
        if (!mounted) { s.getTracks().forEach((t) => t.stop()); return; }
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;

        // Mic level analysis
        try {
          const AudioCtx = window.AudioContext || window.webkitAudioContext;
          const ac = new AudioCtx();
          noiseCtxRef.current = ac;
          const source = ac.createMediaStreamSource(s);
          const analyser = ac.createAnalyser();
          analyser.fftSize = 1024;
          source.connect(analyser);
          const data = new Uint8Array(analyser.frequencyBinCount);
          let recentLoudFrames = 0;
          let voiceActivityBurst = 0;
          noiseTimerRef.current = setInterval(() => {
            analyser.getByteTimeDomainData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) {
              const v = (data[i] - 128) / 128;
              sum += v * v;
            }
            const rms = Math.sqrt(sum / data.length);
            // Feb 2026 tuning: log a lightweight `voice_activity` event on
            // any short 2 s burst (catches brief exchanges like "just a
            // second") AND still fire the harder `noise_spike` on 3 s of
            // sustained speech (was 6 s — too lenient).
            if (rms > 0.08) {
              recentLoudFrames += 1;
              voiceActivityBurst += 1;
              if (recentLoudFrames === 2) {
                logEvent("voice_activity", { rms: rms.toFixed(3), duration_s: 2 });
              }
              if (recentLoudFrames > 3) {  // ~3 sec of sustained speech
                logEvent("noise_spike", { rms: rms.toFixed(3), duration_s: recentLoudFrames });
                flash("Sustained voice / background noise detected. Please remain silent during the test.");
                recentLoudFrames = 0;
              }
            } else {
              recentLoudFrames = Math.max(0, recentLoudFrames - 1);
              voiceActivityBurst = 0;
            }
          }, 1000);
        } catch (e) { /* mic optional */ }

        // Periodic 20-second audio slice capture → Sarvam diarisation.
        // Fires every 60 s while the assessment is active, so live "second
        // voice present" detection works on MCQ + Role Play just like on
        // the Sales Pitch post-submit path.
        //
        // iter43 — hardened against the cross-section race that was
        // surfacing a React dev-overlay with
        //   "Failed to execute 'start' on 'MediaRecorder': NotSupportedError"
        // on MCQ submit. The error fires when the previous section's
        // audio tracks have transitioned to `ended` state (parent stream
        // was stopped) but a pending `startSlice` timer still fires and
        // tries to new-up / start a MediaRecorder on dead tracks. The fix:
        //   1. Guard with a `mounted` flag + track-liveness check.
        //   2. Wrap `rec.start()` in try/catch so the error can never
        //      bubble to React's error boundary (the slice is best-effort —
        //      a failed slice must not break the MCQ submit screen).
        //   3. Track the live recorder in `activeRecorderRef` and stop it
        //      on cleanup so no instance from a previous section is still
        //      recording when the next section tries to start its own.
        //   4. Track BOTH the first-slice setTimeout and the recurring
        //      setInterval in refs so cleanup can clear them.
        try {
          if (typeof MediaRecorder !== "undefined") {
            const audioOnly = new MediaStream(s.getAudioTracks());
            const pickMime = () => {
              const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
              for (const m of cands) {
                if (MediaRecorder.isTypeSupported?.(m)) return m;
              }
              return "";
            };
            const mime = pickMime();
            const startSlice = () => {
              // Short-circuit if the hook has been unmounted (section
              // navigation in progress) or the audio tracks have ended.
              if (!mounted) return;
              // Pause the slice recorder while MicCheckGate owns the mic
              // (iter45) — two concurrent MediaRecorders on the same
              // physical mic cause mobile browsers to mute the second
              // opener, which the user sees as a frozen waveform + a
              // sub-15 KB recording that trips the enrolment-quality gate.
              if (isMicInUse()) return;
              const tracks = audioOnly.getAudioTracks();
              if (!tracks.length || tracks.some((t) => t.readyState !== "live")) return;
              // Also skip if a previous slice recorder is still active —
              // we never want two concurrent recorders on the same stream.
              if (activeRecorderRef.current && activeRecorderRef.current.state !== "inactive") return;
              let rec;
              try {
                rec = mime ? new MediaRecorder(audioOnly, { mimeType: mime }) : new MediaRecorder(audioOnly);
              } catch (_) { return; }
              const chunks = [];
              rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
              rec.onstop = async () => {
                if (activeRecorderRef.current === rec) activeRecorderRef.current = null;
                try {
                  const blob = new Blob(chunks, { type: mime || "audio/webm" });
                  if (blob.size < 2000) return;
                  const fd = new FormData();
                  fd.append("assignment_id", assignmentId);
                  fd.append("source", "mcq_slice");
                  fd.append("audio", blob, `slice-${Date.now()}.webm`);
                  await api.post("/proctor/audio-slice", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                } catch (_) { /* silent — supplementary integrity signal */ }
              };
              rec.onerror = () => {
                if (activeRecorderRef.current === rec) activeRecorderRef.current = null;
              };
              try {
                rec.start();
              } catch (_) {
                // NotSupportedError / InvalidStateError — tracks were
                // ended between the liveness check and start(), or the
                // UA refused this configuration. Silently drop this
                // slice; the next 60s interval will try again if we're
                // still mounted.
                return;
              }
              activeRecorderRef.current = rec;
              setTimeout(() => {
                try {
                  if (rec.state && rec.state !== "inactive") rec.stop();
                } catch (_) { /* noop */ }
              }, 20000);
            };
            // First slice after 30 s warm-up, then every 60 s. Both timers
            // tracked in refs so cleanup clears them even if the candidate
            // navigates between sections before the first slice fires.
            firstSliceTimerRef.current = setTimeout(startSlice, 30000);
            audioSliceTimerRef.current = setInterval(startSlice, 60000);
          }
        } catch (e) { /* mic-slice optional */ }

        // Periodic frame capture
        frameTimerRef.current = setInterval(captureFrame, frameIntervalSec * 1000);
        // First frame after 4s warmup
        setTimeout(captureFrame, 4000);
      } catch (e) {
        flash("Camera/mic access required to take the assessment.");
      }
    };
    start();

    const onBlur = () => { logEvent("tab_blur"); flash("Tab switching is logged. Please return to the test."); };
    const onVis = () => { if (document.hidden) onBlur(); };
    const onPaste = (e) => {
      // Block paste in inputs/textareas (open-response is the target)
      if (e.target && (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT")) {
        e.preventDefault();
        logEvent("paste_blocked");
        flash("Pasting is disabled during the assessment.");
      }
    };
    const onContext = (e) => {
      e.preventDefault();
      logEvent("right_click_blocked");
    };
    const onFsChange = () => {
      if (!document.fullscreenElement) {
        logEvent("fullscreen_exit");
        flash("Exited fullscreen. The assessment requires fullscreen mode.");
      }
    };
    // Screenshot heuristic — Windows PrintScreen, plus macOS Cmd+Shift+3/4/5/6.
    // macOS intercepts these shortcuts in its Screencapture service so they
    // rarely make it to `document.keydown`; we register at WINDOW level in the
    // capture phase AND listen on both keydown+keyup to catch any residual
    // event. The candidate watermark (rendered in every assessment page) is
    // the real defense against sharing — this event just gives admins a flag.
    const isShotKey = (e) => {
      if (e.key === "PrintScreen" || e.keyCode === 44) return "PrintScreen";
      // macOS: Cmd+Shift+ 3|4|5|6 (screenshots); Ctrl variant copies to clipboard
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && ["3", "4", "5", "6"].includes(e.key)) {
        return "macOS-cmd-shift";
      }
      // Windows: Win+Shift+S (Snipping Tool)
      if (e.shiftKey && (e.metaKey || e.key === "Meta") && ["s", "S"].includes(e.key)) {
        return "windows-snip";
      }
      return null;
    };
    let lastShotAt = 0;
    const fireShot = (via, key) => {
      // Dedup rapid fire-events from the same physical key press
      const now = Date.now();
      if (now - lastShotAt < 1500) return;
      lastShotAt = now;
      logEvent("screenshot_suspected", { via, key });
      flash("Screenshots are not permitted during the assessment. This has been logged.");
    };
    const onKeyDown = (e) => {
      const via = isShotKey(e);
      if (via) fireShot(via, e.key);
    };
    const onKeyUp = (e) => {
      const via = isShotKey(e);
      if (via) fireShot(via, e.key);
    };

    window.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVis);
    document.addEventListener("paste", onPaste, true);
    document.addEventListener("contextmenu", onContext);
    document.addEventListener("fullscreenchange", onFsChange);
    // Register at WINDOW level in capture phase so we run before any inner
    // handler can call stopPropagation. Also listen on keyup as a fallback
    // for macOS where the OS eats the keydown event.
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);

    return () => {
      mounted = false;
      unsubBus();
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVis);
      document.removeEventListener("paste", onPaste, true);
      document.removeEventListener("contextmenu", onContext);
      document.removeEventListener("fullscreenchange", onFsChange);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      if (frameTimerRef.current) clearInterval(frameTimerRef.current);
      if (noiseTimerRef.current) clearInterval(noiseTimerRef.current);
      if (audioSliceTimerRef.current) clearInterval(audioSliceTimerRef.current);
      if (firstSliceTimerRef.current) clearTimeout(firstSliceTimerRef.current);
      // Stop any active slice recorder BEFORE releasing the parent stream
      // so the next section's MediaRecorder.start() doesn't fail with
      // NotSupportedError (iter43 fix).
      if (activeRecorderRef.current) {
        try {
          if (activeRecorderRef.current.state && activeRecorderRef.current.state !== "inactive") {
            activeRecorderRef.current.stop();
          }
        } catch (_) { /* noop */ }
        activeRecorderRef.current = null;
      }
      if (noiseCtxRef.current) try { noiseCtxRef.current.close(); } catch (e) { /* noop */ }
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, assignmentId]);

  // Typing burst detector — exposed for caller to wrap textarea onChange
  const watchTypingBurst = useCallback(() => {
    const state = { lastLen: 0, lastTs: Date.now() };
    return (newValue) => {
      const now = Date.now();
      const lenDelta = (newValue?.length || 0) - state.lastLen;
      const dt = (now - state.lastTs) / 1000;
      if (lenDelta > 200 && dt < 2) {
        // 200+ chars in <2s = paste-like
        logEvent("typing_burst", { chars: lenDelta, seconds: dt.toFixed(2) });
        flash("Unusually fast text input detected.");
      }
      state.lastLen = newValue?.length || 0;
      state.lastTs = now;
    };
  }, [logEvent, flash]);

  return { warnings, lastFlash, autoSubmit, videoRef, canvasRef, watchTypingBurst, logEvent };
}
